/**
 * Premium "Countdown Intro" — 3 · 2 · 1 · GO with progress ring.
 *
 * Visual design:
 *  - SVG ring counts down clockwise around a giant number.
 *  - Each digit (3, 2, 1) crashes in with scale + blur, holds, then exits
 *    upward with motion-blur ghost. "GO!" lands with a flash + headline.
 *  - Tiny ticks around the ring pulse on each digit change.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml,
  makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";

export const countdownIntro: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";
  const headline = inputs.text ?? "Allyver geht live";
  const sub = inputs.subtext ?? "";
  const numSize = typeSize(isVertical ? "mega" : "mega", ctx.format) * 1.1;
  const headSize = typeSize(isVertical ? "hero" : "mega", ctx.format) * 0.55;
  const subSize = typeSize("md", ctx.format) * 0.55;
  const ringSize = isVertical ? 720 : 620;
  const ringR = (ringSize - 24) / 2;
  const ringC = 2 * Math.PI * ringR;

  const t = makeTl(clipStartSec);
  const wrapId = `${clipPrefix}-wrap`;
  const ringFgId = `${clipPrefix}-ringfg`;
  const flashId = `${clipPrefix}-flash`;
  const headId = `${clipPrefix}-head`;
  const subId = `${clipPrefix}-sub`;
  const eyeId = `${clipPrefix}-eye`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const beat = 0.5; // seconds per countdown beat
  const digits = ["3", "2", "1", "GO"] as const;
  const digitSpans = digits
    .map(
      (d, i) =>
        `<div id="${clipPrefix}-d${i}" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${i === 3 ? numSize * 0.62 : numSize}px;line-height:0.9;letter-spacing:${i === 3 ? -3 : -8}px;color:${i === 3 ? accent : themeColors.fg};text-shadow:0 0 80px ${withAlpha(i === 3 ? accent : themeColors.fg, 0.5)};">${escapeHtml(d)}</div>`,
    )
    .join("");

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;overflow:hidden;">
  <div style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 28 : 22}px;padding:96px;">
    <div id="${eyeId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${subSize * 0.85}px;letter-spacing:8px;text-transform:uppercase;color:${accent};opacity:0;">— Countdown —</div>

    <div style="position:relative;width:${ringSize}px;height:${ringSize}px;display:flex;align-items:center;justify-content:center;">
      <svg width="${ringSize}" height="${ringSize}" viewBox="0 0 ${ringSize} ${ringSize}" style="position:absolute;inset:0;transform:rotate(-90deg);filter:drop-shadow(0 0 30px ${withAlpha(accent, 0.55)});">
        <circle cx="${ringSize / 2}" cy="${ringSize / 2}" r="${ringR}" fill="none" stroke="${withAlpha(themeColors.fg, 0.12)}" stroke-width="10" />
        <circle id="${ringFgId}" cx="${ringSize / 2}" cy="${ringSize / 2}" r="${ringR}" fill="none" stroke="${accent}" stroke-width="10" stroke-linecap="round" stroke-dasharray="${ringC}" stroke-dashoffset="${ringC}" />
      </svg>
      ${digitSpans}
    </div>

    <div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headSize}px;line-height:0.95;letter-spacing:-2.5px;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:${isVertical ? 880 : 1400}px;text-shadow:0 0 50px ${withAlpha(accent, 0.45)};opacity:0;">${escapeHtml(headline)}</div>
    ${sub ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subSize}px;letter-spacing:0.4px;color:${withAlpha(themeColors.fg, 0.78)};text-align:center;max-width:${isVertical ? 760 : 1100}px;line-height:1.35;opacity:0;">${escapeHtml(sub)}</div>` : ""}
  </div>
  <div id="${flashId}" style="position:absolute;inset:0;background:${themeColors.fg};opacity:0;pointer-events:none;mix-blend-mode:screen;"></div>
</div>`;

  const initParts: string[] = [
    `gsap.set("#${ringFgId}", { attr: { "stroke-dashoffset": ${ringC} } });`,
    `gsap.set("#${eyeId}", { opacity: 0, y: -16 });`,
    `gsap.set("#${headId}", { opacity: 0, y: 28 });`,
    sub ? `gsap.set("#${subId}", { opacity: 0, y: 18 });` : "",
  ];
  digits.forEach((_, i) => {
    initParts.push(`gsap.set("#${clipPrefix}-d${i}", { opacity: 0, scale: ${i === 3 ? 1.6 : 2.0}, filter: "blur(18px)" });`);
  });
  const init = initParts.join("\n");

  const tweens: string[] = [
    t.to(`#${eyeId}`, { opacity: 1, y: 0, duration: 0.4, ease: "raw:\"power2.out\"" }, 0.05),
  ];

  // Ring fills over the 4 beats then drains for GO.
  tweens.push(t.to(`#${ringFgId}`, { attr: { "stroke-dashoffset": 0 }, duration: beat * 3, ease: "raw:\"none\"" }, 0.1));

  // Each digit pops in/out on the beat.
  digits.forEach((_, i) => {
    const at = 0.1 + i * beat;
    tweens.push(t.to(`#${clipPrefix}-d${i}`, { opacity: 1, scale: 1, filter: "blur(0px)", duration: 0.22, ease: "raw:\"back.out(2.0)\"" }, at));
    if (i < digits.length - 1) {
      // Exit upward with motion blur.
      tweens.push(t.to(`#${clipPrefix}-d${i}`, { opacity: 0, y: -120, filter: "blur(14px)", scale: 0.7, duration: 0.22, ease: "raw:\"power3.in\"" }, at + beat - 0.22));
    }
  });

  const goAt = 0.1 + 3 * beat;
  // Flash on GO.
  tweens.push(t.to(`#${flashId}`, { opacity: 0.85, duration: 0.06, ease: "raw:\"power2.out\"" }, goAt));
  tweens.push(t.to(`#${flashId}`, { opacity: 0, duration: 0.4, ease: "raw:\"power2.in\"" }, goAt + 0.06));
  // Ring quickly drains as GO.
  tweens.push(t.to(`#${ringFgId}`, { attr: { "stroke-dashoffset": ringC }, duration: 0.5, ease: "raw:\"power3.out\"" }, goAt));
  // Headline + sub appear after GO.
  tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power3.out\"" }, goAt + 0.18));
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, goAt + 0.38));

  // Subtle GO breath.
  const dwell = Math.max(0.5, clipDurationSec - (goAt + 0.6));
  tweens.push(t.to(`#${clipPrefix}-d3`, { scale: 1.05, duration: dwell, ease: "raw:\"sine.inOut\"" }, goAt + 0.5));

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
